import { useMemo, type FunctionComponent } from "react";
import { Empty, List } from "antd";
import { useTranslation } from "react-i18next";

import { type ChatStatus } from "@fitness-app/data-models/entities/Chat";

import ChatChannelItem from "~/modules/Chat/ChatChannelItem";
import { sortChannelsByUrgency, useChannelResponseMetrics } from "~/modules/Chat/hooks/useChannelResponseMetrics";
import { usePendingChatSuggestionChannels } from "~/modules/Chat/hooks/usePendingChatSuggestionChannels";

interface OwnProps {
  changeTab: (status: ChatStatus) => void;
  sortByResponseTime?: boolean;
}

type Props = OwnProps;

const PendingChatSuggestionsList: FunctionComponent<Props> = ({ changeTab, sortByResponseTime }) => {
  const { t } = useTranslation(["dashboard"]);
  const { data, isLoading } = usePendingChatSuggestionChannels();

  const channelIds = useMemo(() => data?.channels.map((channel) => channel.id) || [], [data?.channels]);
  const { data: responseMetrics } = useChannelResponseMetrics(channelIds);

  const sortedChannels = useMemo(() => {
    if (!sortByResponseTime || !responseMetrics) {
      return data?.channels || [];
    }

    return sortChannelsByUrgency(data?.channels || [], responseMetrics);
  }, [data?.channels, responseMetrics, sortByResponseTime]);

  return (
    <List
      itemLayout="horizontal"
      dataSource={sortedChannels}
      loading={isLoading}
      renderItem={(channel) => (
        <ChatChannelItem
          channel={channel}
          changeTab={changeTab}
          archived={false}
          responseMetric={responseMetrics?.[channel.id]}
        />
      )}
      locale={{
        emptyText: <Empty className="mt-12" description={t("chatDrawer.emptyPendingConfirmationList")} />,
      }}
      style={{ minHeight: 500 }}
      className="max-h-full overflow-y-auto px-4"
    />
  );
};

export default PendingChatSuggestionsList;
